/**
 * One consent decision gates ALL tracking stacks (master plan §5.2 / C-C1+C-C3):
 * Meta pixel, Meta CAPI, GA4/Google tags, Google server uploads, and Clarity all
 * read the SAME `ps_privacy` cookie through this module. Denied means full
 * suppression everywhere — LDU or any per-platform half-measure is never a
 * substitute for honoring an opt-out.
 *
 * State model:
 *  - cookie absent  → 'granted' for US traffic (US state laws are opt-out
 *    regimes; the proxy pre-sets 'denied' for gated geos before first paint)
 *  - 'granted' / 'denied' → explicit state, set by the proxy (geo/GPC) or the
 *    Your Privacy Choices panel
 *  - A Global Privacy Control signal ALWAYS wins over a stored 'granted' —
 *    12+ US states require honoring it as an automatic opt-out.
 *
 * Edge-safe on purpose (no node imports): the proxy runs this at the edge, and
 * client components import the same constants so the two can never drift.
 */

export const PS_PRIVACY_COOKIE = 'ps_privacy'
export const PS_PRIVACY_TTL_DAYS = 365

export type ConsentState = 'granted' | 'denied'

/**
 * Countries whose visitors get tracking suppressed entirely instead of a
 * consent banner (master plan §5.2: geo-gate EU/UK/EEA/CH — a US-focused
 * business with negligible EU revenue suppresses rather than builds an opt-in
 * CMP). EU-27 + EEA (IS, LI, NO) + UK + CH.
 */
export const GATED_COUNTRIES: ReadonlySet<string> = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
])

export function parseConsentValue(
  value: string | undefined | null
): ConsentState {
  return value === 'denied' ? 'denied' : 'granted'
}

/** `Sec-GPC: 1` is the only defined value of the header. */
export function isGpcHeaderValue(value: string | undefined | null): boolean {
  return value === '1'
}

/**
 * Resolve consent from a server request: stored cookie, overridden to denied by
 * a GPC header. Works with any header/cookie accessor shape (NextRequest,
 * next/headers) so route handlers and the proxy share one rule.
 */
export function resolveServerConsent(input: {
  cookieValue: string | undefined | null
  gpcHeader: string | undefined | null
}): ConsentState {
  if (isGpcHeaderValue(input.gpcHeader)) return 'denied'
  return parseConsentValue(input.cookieValue)
}

/**
 * Client-side consent check — synchronous so script components can decide at
 * mount whether to inject anything at all. GPC (navigator.globalPrivacyControl)
 * overrides a stored grant, mirroring the server rule.
 */
export function readClientConsent(): ConsentState {
  if (typeof document === 'undefined') return 'denied'
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean }
  if (nav.globalPrivacyControl) return 'denied'
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${PS_PRIVACY_COOKIE}=([^;]*)`)
  )
  return parseConsentValue(match?.[1])
}

/** True when the browser is broadcasting Global Privacy Control. */
export function clientHasGpc(): boolean {
  if (typeof navigator === 'undefined') return false
  return Boolean(
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl
  )
}

/**
 * Write the consent cookie from the client (Your Privacy Choices panel).
 * The caller is responsible for reloading so every already-mounted tracker
 * unloads — full suppression, not best-effort teardown.
 */
export function writeClientConsent(state: ConsentState): void {
  if (typeof document === 'undefined') return
  const expiry = new Date()
  expiry.setDate(expiry.getDate() + PS_PRIVACY_TTL_DAYS)
  document.cookie = `${PS_PRIVACY_COOKIE}=${state}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax; Secure`
}
